import { LitElement, html, nothing, unsafeCSS, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import styles from './csv-importer.css?inline';

import type {
  ImportErrorDetail,
  ImportDoneDetail,
  ImportCancelDetail,
  ImportResult,
  OnImportSubmit,
} from '../../types/template';
import type {
  SetupView,
  PreviewUploadView,
  ConfirmView,
  CancelConfirmView,
} from '../../types/views';
import {
  ERROR_CODES,
  DEFAULT_TEMPLATE_TIMEOUT_MS,
  DEFAULT_PROCESSING_DELAY_MS,
  MIN_PROCESSING_DISPLAY_MS,
} from '../../constants/default';
import { formatFileSize } from '../../utils/format-file-size';
import { STEPS, type StepId } from '../../constants/steps';
import { NEXT_BLOCKING_VIEWS } from '../../constants/views';
import { TemplateLoaderController } from '../../controllers/template-loader-controller';
import { SubmitController } from '../../controllers/submit-controller';
import { downloadTemplateCsv } from '../../utils/download-template-csv';
import { mapPreviewReport } from '../../services/preview-report-mapper';
import { mapConfirmReport } from '../../services/confirm-report-mapper';
import {
  generateErrorReportCSV,
  triggerBrowserDownload,
} from '../../services/error-report';

import type {
  CsvImportSetupBodyFileStateEvent,
  RequiredField,
} from '../csv-import-setup/csv-import-setup-body';
import { setupStepTemplate } from '../csv-import-setup/csv-import-setup-step';
import { reviewStepTemplate } from '../csv-import-review/csv-import-review-step';
import {
  EMPTY_VALIDATION_SUMMARY,
  type ReviewValidationSummary,
} from '../csv-import-validation-summary/csv-import-validation-summary';
import { confirmStepTemplate } from '../csv-import-confirm/csv-import-confirm-step';

import '@inmar/component-lib';
import '../csv-error-modal/csv-error-modal';
import '../csv-import-modal-shell/csv-import-modal-shell';
import { cancelConfirmPanelTemplate } from '../csv-import-cancel-confirm/cancel-confirm-panel';
import { ParserController } from '../../controllers/parser-controller';
import { computeConfirmSummary } from './csv-importer-confirm-view-derivation';

// ============================================================================
// Types
// ============================================================================

export interface CsvImporterImportErrorEvent extends ImportErrorDetail {
  element: CsvImporter;
}

/**
 * Internal orchestrator type for the cancel-confirmation panel.
 * null = no confirmation needed, close immediately.
 * Non-null = the panel is shown with the corresponding view.
 */
type CancelConfirmViewInternal = CancelConfirmView | null;

// ============================================================================
// Global augmentations
// ============================================================================

declare global {
  interface HTMLElementEventMap {
    'import-error': CustomEvent<CsvImporterImportErrorEvent>;
    'import-done': CustomEvent<ImportDoneDetail>;
    'import-cancel': CustomEvent<ImportCancelDetail>;
  }
  interface HTMLElementTagNameMap {
    'csv-importer': CsvImporter;
  }
}

/**
 * Entry component for the CSV import workflow.
 *
 * Renders an "Import" button that, when clicked, fetches and validates the
 * template at `template-url`. On failure, opens an error modal and emits an
 * `import-error` event. On success, opens the multi-step import workflow.
 *
 * The orchestrator owns all workflow state (`_currentStep`, `_selectedFile`,
 * `_fileError`) and computes derived view props for each body component.
 *
 * @fires import-error - Fired when template loading or validation fails.
 *   Detail: `{ code: ValidationErrorCode; errors: ValidationError[]; element: CsvImporter }`
 *
 * @example
 * ```html
 * <csv-importer
 *   template-url="/templates/new-hires.json"
 *   button-label="Import New Hires"
 * ></csv-importer>
 * ```
 */
@customElement('csv-importer')
export class CsvImporter extends LitElement {
  static readonly styles = [unsafeCSS(styles)];

  // ============================================================================
  // Properties
  // ============================================================================

  /** URL to the template JSON file. */
  @property({ type: String, attribute: 'template-url' })
  templateUrl = '';

  /** Label displayed on the import button. */
  @property({ type: String, attribute: 'button-label' })
  buttonLabel = 'Import';

  /** Maximum time (ms) to wait for the template to load before showing a timeout error. */
  @property({ type: Number, attribute: 'template-timeout-ms' })
  templateTimeoutMs = DEFAULT_TEMPLATE_TIMEOUT_MS;

  /**
   * Host-provided callback invoked when the user confirms an import.
   *
   * Forward declaration only — not wired to any logic in this PR.
   * Future runtime checks should use `typeof this.onImportSubmit !== 'function'`
   * to handle undefined, null, and invalid assignments in one branch.
   *
   * Plain class field — no decorator needed since callbacks don't trigger re-renders.
   */
  onImportSubmit?: OnImportSubmit;

  // ============================================================================
  // Internal state
  // ============================================================================

  private _templateLoader = new TemplateLoaderController(this);
  private _parser = new ParserController(this);
  private _submit = new SubmitController(this, DEFAULT_PROCESSING_DELAY_MS);
  /** Current step in the multi-step flow. `null` means modal is closed. */
  @state() private _currentStep: StepId | null = null;

  /** The file selected by the user in the setup step. */
  @state() private _selectedFile: File | null = null;

  /** File-validation error message from the upload area. */
  @state() private _fileError: string | null = null;

  /**
   * Distinguishes between format errors (invalid type/size) and upload errors
   * (server-side failure). Determines which error view to show.
   */
  @state() private _fileErrorType: 'format' | 'upload' | null = null;

  /**
   * Pre-computed required fields from the loaded template.
   *
   * Intentionally NOT @state() — always assigned in the same synchronous block
   * as `_currentStep = 'select-file'` (which triggers re-render). A separate
   * @state() would cause a redundant render cycle with no benefit.
   */
  private _requiredFields: RequiredField[] = [];

  /** Whether the cancel confirmation overlay is visible. */
  @state() private _showCancelConfirm = false;

  /** Tracks whether the final error report has been downloaded (confirm step). */
  @state() private _errorReportDownloaded = false;

  /** Whether the template CSV download failed. */
  @state() private _templateDownloadError = false;

  /** Whether the preview-step error report download failed. */
  @state() private _previewErrorReportDownloadFailed = false;

  /** Whether the confirm-step final error report download failed. */
  @state() private _confirmReportDownloadFailed = false;

  /** Whether the cancel-confirm overlay download succeeded (shows success message). */
  @state() private _cancelConfirmDownloadSuccess = false;

  /** Screen-reader announcement for the preview error report download. Cleared after 1s. */
  @state() private _previewErrorReportDownloadAnnouncement: string | null =
    null;

  /**
   * True while the minimum processing display delay is active.
   * Keeps the 'processing' view visible even after the async operation
   * completes, preventing a jarring flash for fast operations.
   */
  @state() private _isProcessingDelay = false;

  /**
   * True when the host's onImportSubmit returned { status: 'accepted' },
   * indicating the import was accepted for async background processing.
   * Drives _confirmView to return 'accepted' with highest precedence.
   */
  @state() private _isAcceptedSubmit = false;

  // ============================================================================
  // Derived view getters
  // ============================================================================

  private get _setupView(): SetupView {
    if (this._parser?.error) return 'preview-error';
    if (this._parser?.isRunning || this._isProcessingDelay) return 'processing';

    if (this._templateDownloadError) return 'template-download-error';
    if (this._fileError) {
      return this._fileErrorType === 'upload'
        ? 'file-upload-error'
        : 'file-format-error';
    }
    if (this._selectedFile) return 'file-selected';
    return 'idle';
  }

  private get _previewUploadView(): PreviewUploadView {
    if (this._submit.error) return 'import-error';
    if (this._previewErrorReportDownloadFailed)
      return 'error-report-download-error';
    if (this._submit.isRunning && this._submit.isDelayed)
      return 'processing-delay';
    if (this._submit.isRunning || this._isProcessingDelay) return 'processing';

    const result = this._parser.validationResult;
    if (result && result.validCount === 0) return 'all-invalid';
    if (result && result.invalidCount + result.duplicateCount > 0)
      return 'partial-valid';
    return 'all-valid';
  }

  private get _confirmView(): ConfirmView {
    if (this._isAcceptedSubmit) return 'accepted';
    if (this._confirmReportDownloadFailed)
      return 'import-report-download-error';

    const serverErrors =
      this._submit.response?.status === 'completed'
        ? (this._submit.response.errors ?? [])
        : [];
    const clientResult = this._parser.validationResult;
    const combinedErrors =
      (clientResult?.invalidCount ?? 0) +
      (clientResult?.duplicateCount ?? 0) +
      serverErrors.length;

    if (combinedErrors > 0) return 'partial-success';
    return 'success';
  }

  /**
   * Computes merged confirm step summary from server and client counts.
   */
  private get _confirmSummary(): ReviewValidationSummary {
    const response = this._submit.response;
    const clientResult = this._parser.validationResult;

    const serverErrors =
      response?.status === 'completed' ? response.errors : undefined;
    const serverSummary =
      response?.status === 'completed' ? response.summary : undefined;

    return computeConfirmSummary(clientResult, serverSummary, serverErrors);
  }

  /**
   * Determines which cancel confirmation view to show, or null if no confirmation needed.
   *
   * Precedence (highest first):
   * 1. Setup step (no progress) → null
   * 2. Preview step + processing or processing-delay view → null (closes without confirmation)
   * 3. Preview step (has Cancel button) → 'cancel-import'
   * 4. Confirm step + report not downloaded + download not failed → 'download-report'
   * 5. Confirm step (success, report downloaded, or download failed) → null (fires import-done)
   *
   * EXTENSIBILITY: Add new conditions here without modifying _onClose or the overlay component.
   */
  private get _cancelConfirmView(): CancelConfirmViewInternal {
    // No confirmation needed on setup step - user hasn't made progress
    if (this._currentStep === 'select-file') {
      return null;
    }

    // Preview step logic
    if (this._currentStep === 'preview-upload') {
      // Processing views have no confirmation - close immediately
      if (this._previewUploadView === 'processing-delay') {
        return null;
      }
      if (this._previewUploadView === 'processing') {
        return null;
      }
      // All other preview views have Cancel button - show confirmation
      return 'cancel-import';
    }

    // Confirm step gating (Req 18):
    // 1. 'success' view → direct close (no error report exists)
    // 2. _confirmReportDownloadFailed → direct close (user already saw error view)
    // 3. _cancelConfirmDownloadSuccess → show success message overlay
    // 4. _errorReportDownloaded → direct close (report already downloaded)
    // 5. Otherwise → show 'download-report' overlay
    if (this._currentStep === 'confirm') {
      if (this._confirmView === 'accepted') return null;
      if (this._confirmView === 'success') return null;
      if (this._confirmReportDownloadFailed) return null;
      if (this._cancelConfirmDownloadSuccess) return 'download-report-success';
      if (this._errorReportDownloaded) return null;
      return 'download-report';
    }

    // Null step - close directly
    return null;
  }

  // ============================================================================
  // Event handlers
  // ============================================================================

  private async _onImportClick() {
    if (this._templateLoader.isRunning) return;

    const outcome = await this._templateLoader.load(
      this.templateUrl,
      this.templateTimeoutMs,
    );

    if (outcome === 'aborted') return;

    // outcome === 'completed'
    if (this._templateLoader.template) {
      this._requiredFields = this._templateLoader.template.columns
        .filter((c) => c.required)
        .map((c) => ({ name: c.label, description: c.description }));
      this._currentStep = 'select-file';
      return;
    }

    if (this._templateLoader.error) {
      this.dispatchEvent(
        new CustomEvent<CsvImporterImportErrorEvent>('import-error', {
          bubbles: true,
          composed: true,
          detail: {
            code:
              this._templateLoader.error.errors[0]?.code ??
              ERROR_CODES.UNKNOWN_ERROR,
            errors: this._templateLoader.error.errors,
            element: this,
          },
        }),
      );
    }
  }

  private _onErrorModalClosed() {
    this._templateLoader.reset();
    this._requiredFields = [];
  }

  private _onFileState(e: CustomEvent<CsvImportSetupBodyFileStateEvent>) {
    this._selectedFile = e.detail.file;
    this._fileError = e.detail.error;
    // Only local validation errors (type/size) reach here — always 'format'.
    // The 'upload' type and 'file-upload-error' view exist as defensive scaffolding.
    this._fileErrorType = e.detail.error ? 'format' : null;
    if (!e.detail.file) {
      this._parser.reset();
    }
  }

  private async _onNext() {
    if (this._currentStep === null) return;

    if (this._currentStep === 'select-file') {
      if (
        !this._selectedFile ||
        this._fileError ||
        !this._templateLoader.template
      ) {
        return;
      }

      this._isProcessingDelay = true;
      const [result] = await Promise.all([
        this._parser.parse(this._selectedFile, this._templateLoader.template),
        new Promise((resolve) =>
          setTimeout(resolve, MIN_PROCESSING_DISPLAY_MS),
        ),
      ]);
      this._isProcessingDelay = false;
      if (result.status === 'aborted') return;
      if (result.status === 'completed' && !result.error) {
        this._currentStep = 'preview-upload';
      }
      return;
    }

    if (this._currentStep === 'preview-upload') {
      if (NEXT_BLOCKING_VIEWS.has(this._previewUploadView)) return;

      const result = this._parser.validationResult;
      const template = this._templateLoader.template;
      if (!result || !template) return;

      const importResult: ImportResult = {
        rows: result.validRows,
        summary: {
          totalRows: result.totalRows,
          validCount: result.validCount,
          invalidCount: result.invalidCount,
          duplicateCount: result.duplicateCount,
        },
        template,
      };

      this._isProcessingDelay = true;
      const [outcome] = await Promise.all([
        this._submit.invoke(
          importResult,
          this.onImportSubmit ??
            (() => Promise.reject(new Error('onImportSubmit not configured'))),
        ),
        new Promise((resolve) =>
          setTimeout(resolve, MIN_PROCESSING_DISPLAY_MS),
        ),
      ]);
      this._isProcessingDelay = false;
      if (outcome === 'completed') {
        this._currentStep = 'confirm';
      }
      if (outcome === 'accepted') {
        this._isAcceptedSubmit = true;
        this._currentStep = 'confirm';
      }
      // 'rejected' → _previewUploadView shows 'import-error' via getter
      // 'stale' → no-op (modal was dismissed, state already reset)
      return;
    }

    const idx = STEPS.findIndex((s) => s.id === this._currentStep);
    if (idx < STEPS.length - 1) {
      this._currentStep = STEPS[idx + 1]!.id;
    }
  }

  private _onBack() {
    if (this._currentStep === null) return;
    const idx = STEPS.findIndex((s) => s.id === this._currentStep);
    if (idx > 0) {
      this._currentStep = STEPS[idx - 1]!.id;
      if (this._currentStep === 'select-file') {
        this._parser.reset();
        this._submit.reset();
        this._errorReportDownloaded = false;
        this._previewErrorReportDownloadFailed = false;
        this._previewErrorReportDownloadAnnouncement = null;
      }
    }
  }

  private _onUploadNewFile() {
    this._parser.reset();
    this._submit.reset();
    this._selectedFile = null;
    this._fileError = null;
    this._fileErrorType = null;
    this._errorReportDownloaded = false;
    this._previewErrorReportDownloadFailed = false;
    this._previewErrorReportDownloadAnnouncement = null;
    this._currentStep = 'select-file';
  }

  /**
   * Handles close attempts from the workflow shell (Cancel button or X button).
   * Delegates to _cancelConfirmView to determine whether confirmation is needed.
   */
  private _onClose() {
    // Req 7.4: While the inline cancel-confirmation panel is showing, Escape/X
    // fires csv-import-close again. Treat that as dismiss (equivalent to "No,
    // continue importing") rather than re-triggering the panel.
    if (this._showCancelConfirm) {
      this._onCancelConfirmNo();
      return;
    }

    const view = this._cancelConfirmView;
    if (view === null) {
      if (this._currentStep === 'confirm') {
        this._onConfirmClose();
      } else if (
        this._currentStep === 'preview-upload' &&
        (this._previewUploadView === 'processing' ||
          this._previewUploadView === 'processing-delay')
      ) {
        this._onProcessingClose();
      } else {
        this._resetWorkflowState();
      }
      return;
    }

    this._showCancelConfirm = true;
  }

  /**
   * Handles close from confirm step when no confirmation overlay is needed.
   * Fires import-done since user is finishing a successful import.
   * Per Req 17.3, outcome is always 'success' — confirm step is only reached after successful submission.
   */
  private _onConfirmClose() {
    this._resetWorkflowState();
    this.dispatchEvent(
      new CustomEvent<ImportDoneDetail>('import-done', {
        bubbles: true,
        composed: true,
        detail: { outcome: 'success', element: this },
      }),
    );
  }

  /**
   * Handles close during processing or processing-delay views.
   * Resets workflow state and fires import-done with outcome 'pending'
   * to signal that background processing continues.
   */
  private _onProcessingClose() {
    this._resetWorkflowState();
    this.dispatchEvent(
      new CustomEvent<ImportDoneDetail>('import-done', {
        bubbles: true,
        composed: true,
        detail: { outcome: 'pending', element: this },
      }),
    );
  }

  /**
   * Handles "Yes" confirmation from the inline cancel-confirmation panel.
   * Dispatches import-cancel for cancel-import view, import-done for download-report views.
   */
  private _onCancelConfirmYes() {
    // IMPORTANT: Capture view BEFORE reset, since getters depend on _currentStep
    const view = this._cancelConfirmView;
    const outcome = 'success'; // Confirm step always fires 'success' per Req 17.3

    this._showCancelConfirm = false;
    this._resetWorkflowState();

    // Dispatch appropriate event based on captured view
    if (view === 'download-report' || view === 'download-report-success') {
      this.dispatchEvent(
        new CustomEvent<ImportDoneDetail>('import-done', {
          bubbles: true,
          composed: true,
          detail: { outcome, element: this },
        }),
      );
    } else {
      this.dispatchEvent(
        new CustomEvent<ImportCancelDetail>('import-cancel', {
          bubbles: true,
          composed: true,
          detail: { element: this },
        }),
      );
    }
  }

  /**
   * Handles "No" / dismiss from the inline cancel-confirmation panel.
   * Hides the panel and preserves workflow state.
   * Focus restoration is handled by the shell's cancel-confirm property logic.
   */
  private _onCancelConfirmNo() {
    this._showCancelConfirm = false;
    this._cancelConfirmDownloadSuccess = false;
  }

  /**
   * Handles "Download" action from the download-report panel view.
   * Triggers download. On success: marks downloaded, panel transitions to success view.
   * On failure: hides panel, sets _confirmReportDownloadFailed = true,
   * returning user to import-report-download-error view.
   */
  private _onCancelConfirmDownload() {
    this._confirmReportDownloadFailed = false;

    if (this._executeConfirmReportDownload()) {
      this._errorReportDownloaded = true;
      this._cancelConfirmDownloadSuccess = true;
      // Panel stays open with success message — user clicks "Close" to dismiss
    } else {
      this._showCancelConfirm = false;
      this._confirmReportDownloadFailed = true;
      // User returns to confirm step showing 'import-report-download-error' view
    }
  }

  /**
   * Resets all workflow state to initial values.
   */
  private _resetWorkflowState() {
    this._currentStep = null;
    this._selectedFile = null;
    this._fileError = null;
    this._fileErrorType = null;
    this._requiredFields = [];
    this._showCancelConfirm = false;
    this._errorReportDownloaded = false;
    this._cancelConfirmDownloadSuccess = false;
    this._previewErrorReportDownloadFailed = false;
    this._previewErrorReportDownloadAnnouncement = null;
    this._templateDownloadError = false;
    this._confirmReportDownloadFailed = false;
    this._isAcceptedSubmit = false;
    this._parser.reset();
    this._submit.reset();
  }

  private _onDownloadTemplate() {
    const template = this._templateLoader.template;
    if (!template) return;

    // Reset error state on retry
    this._templateDownloadError = false;

    try {
      downloadTemplateCsv(template);
    } catch {
      this._templateDownloadError = true;
    }
  }

  private _onDownloadErrors(): void {
    this._previewErrorReportDownloadFailed = false;

    const validationResult = this._parser.validationResult;
    const template = this._templateLoader.template;

    if (!validationResult || !template) {
      this._previewErrorReportDownloadFailed = true;
      this._previewErrorReportDownloadAnnouncement =
        'Error report download failed';
      setTimeout(() => {
        this._previewErrorReportDownloadAnnouncement = null;
      }, 1000);
      return;
    }

    try {
      const { rows, columns } = mapPreviewReport(validationResult, template);
      const blob = generateErrorReportCSV(rows, columns);
      const filename = `${template.importerConfig.title} - Error Report.csv`;
      triggerBrowserDownload(blob, filename);
      this._previewErrorReportDownloadAnnouncement = 'Error report downloaded';
    } catch {
      this._previewErrorReportDownloadFailed = true;
      this._previewErrorReportDownloadAnnouncement =
        'Error report download failed';
    }

    setTimeout(() => {
      this._previewErrorReportDownloadAnnouncement = null;
    }, 1000);
  }

  /**
   * Handles the csv-import-download-confirm-report event.
   * Merges client-side and server-side errors, generates CSV, triggers download.
   * Parallels _onDownloadErrors for the preview step.
   */
  private _downloadConfirmReport(): void {
    this._confirmReportDownloadFailed = false;

    if (this._executeConfirmReportDownload()) {
      this._errorReportDownloaded = true;
      this._confirmReportDownloadFailed = false;
    } else {
      this._confirmReportDownloadFailed = true;
    }
  }

  /**
   * Core download logic for the confirm step's Final Error Report.
   * Merges client-side invalid rows with server-side rejection errors,
   * generates CSV, and triggers the browser download.
   *
   * @returns `true` if the download succeeded, `false` on any failure.
   */
  private _executeConfirmReportDownload(): boolean {
    const validationResult = this._parser.validationResult;
    const template = this._templateLoader.template;

    if (!validationResult || !template) {
      return false;
    }

    try {
      const { rows: clientRows, columns } = mapPreviewReport(
        validationResult,
        template,
      );
      const toFileRow = (zeroBasedIdx: number): number =>
        validationResult.validRowIndices[zeroBasedIdx] ?? zeroBasedIdx + 1;
      const serverErrors =
        this._submit.response?.status === 'completed'
          ? this._submit.response.errors
          : undefined;
      const mergedRows = mapConfirmReport(clientRows, serverErrors, toFileRow);
      const blob = generateErrorReportCSV(mergedRows, columns);
      const filename = `${template.importerConfig.title} - Import Error Report.csv`;
      triggerBrowserDownload(blob, filename);
      return true;
    } catch {
      return false;
    }
  }

  // ============================================================================
  // Render
  // ============================================================================

  override render() {
    return html`
      <inmar-hc-button
        label=${this._templateLoader.isRunning
          ? 'Loading...'
          : this.buttonLabel}
        ?disabled=${this._templateLoader.isRunning}
        @button-click=${this._onImportClick}
      ></inmar-hc-button>

      <csv-error-modal
        ?open=${this._templateLoader.error !== null}
        modal-title="Import"
        heading-text="Bulk upload is currently unavailable."
        body-text="We're having trouble connecting to the upload service. Please submit a help ticket."
        @error-modal-closed=${this._onErrorModalClosed}
      ></csv-error-modal>

      ${this._currentStep !== null && this._templateLoader.template
        ? html`
            <csv-import-modal-shell
              modal-title=${'Import ' +
              this._templateLoader.template.importerConfig.title}
              current-step=${this._currentStep}
              ?cancel-confirm=${this._showCancelConfirm}
              @csv-import-file-state=${this._onFileState}
              @csv-import-next=${this._onNext}
              @csv-import-back=${this._onBack}
              @csv-import-close=${this._onClose}
              @csv-import-download-template=${this._onDownloadTemplate}
              @csv-import-upload-new-file=${this._onUploadNewFile}
              @csv-import-download-errors=${this._onDownloadErrors}
              @csv-import-download-confirm-report=${this._downloadConfirmReport}
            >
              ${this._renderActiveStep()}
            </csv-import-modal-shell>
          `
        : nothing}
    `;
  }

  private _renderActiveStep(): TemplateResult | typeof nothing {
    // Cancel confirmation replaces the step body + footer within the same shell.
    if (this._showCancelConfirm && this._cancelConfirmView !== null) {
      return cancelConfirmPanelTemplate(this._cancelConfirmView, {
        onYes: this._onCancelConfirmYes,
        onNo: this._onCancelConfirmNo,
        onDownload: this._onCancelConfirmDownload,
      });
    }

    switch (this._currentStep) {
      case 'select-file': {
        const maxFileSize =
          this._templateLoader.template!.importerConfig.limits.maxFileSize;
        return setupStepTemplate(this._setupView, {
          selectedFileName: this._selectedFile?.name ?? null,
          fileErrorMessage: this._fileError,
          requiredFields: this._requiredFields,
          maxFileSize,
          maxFileSizeLabel: `(max ${formatFileSize(maxFileSize)})`,
        });
      }
      case 'preview-upload': {
        const result = this._parser.validationResult;
        const summary: ReviewValidationSummary = result
          ? {
              totalRows: result.totalRows,
              validCount: result.validCount,
              invalidCount: result.invalidCount,
              duplicateCount: result.duplicateCount,
            }
          : EMPTY_VALIDATION_SUMMARY;
        return reviewStepTemplate(this._previewUploadView, {
          summary,
          announcement: this._previewErrorReportDownloadAnnouncement,
        });
      }
      case 'confirm':
        return confirmStepTemplate(this._confirmView, {
          summary: this._confirmSummary,
        });
      default:
        return nothing;
    }
  }
}
